import React, { useState } from 'react';
import { Link, useNavigate   } from 'react-router-dom'
import usePurchase from '@/hooks/usePurchase';
import { usePlans } from '@/hooks/usePlans';
import styled from 'styled-components';
import { breakpoints } from '@/layouts/breakpoints';

const BORDER   = '1px solid #e5e7eb';
const DARK_BTN = '#000';
const CHECK    = '#16a34a';
const CROSS    = '#d1d5db';

/* ─── Icons ─── */
const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="9" stroke={CHECK} strokeWidth="1.5"/>
    <path d="M6.5 10.5l2.5 2.5 4.5-5" stroke={CHECK} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CrossIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="9" stroke={CROSS} strokeWidth="1.5"/>
    <path d="M7.5 10h5" stroke={CROSS} strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

/* Up/down chevron matching the screenshot exactly */
const UpDownIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path d="M5 6.5L9 3L13 6.5"  stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M5 11.5L9 15L13 11.5" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CollapseIcon = ({ open }) => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ transform: open ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.2s' }}>
    <path d="M4 11.5L9 6.5L14 11.5" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const renderValue = (val, sub) => {
  if (val === true)  return <CheckIcon />;
  if (val === false) return <CrossIcon />;
  return (
    <span style={{ textAlign: 'center', lineHeight: 1.4 }}>
      {val}
      {sub && <><br /><span style={{ color: '#6b7280', fontSize: 12 }}>{sub}</span></>}
    </span>
  );
};

/* ─── Shared button ─── */
const Btn = styled.button`
  display: block;
  width: 100%;
  padding: 9px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.15s;
  &:hover { opacity: 0.85; }
  background: ${({ $v }) => ($v === 'outline' ? '#fff' : DARK_BTN)};
  color:      ${({ $v }) => ($v === 'outline' ? '#1a1a1a' : '#fff')};
  border:     none !important;
`;

/* ─── Data ─── */
const Skeleton = () => (
  <span style={{ display: 'inline-block', width: 70, height: 16, borderRadius: 4, background: '#e5e7eb', animation: 'skeletonPulse 1.2s ease-in-out infinite' }} />
);

const planDefs = [
  { name: 'Free',       btn: 'Get started', variant: 'outline', id: 'free' },
  { name: 'Starter',    btn: 'Upgrade now', variant: 'dark'   , id: 'starter' },
  { name: 'Pro',        btn: 'Upgrade now', variant: 'dark'   , id: 'pro'},
  { name: 'Enterprise', btn: 'Contact us',  variant: 'outline', id: 'enterprise'},
];

const buildSections = (plans?: any) => [
  {
    id: 'pricing', title: 'Pricing',
    underline: false,
    rows: [
      { label: 'Monthly price', values: ['Free', plans?.displayPrices?.starter?.monthly ? `${plans.displayPrices.starter.monthly}/mo` : <Skeleton />, plans?.displayPrices?.pro?.monthly ? `${plans.displayPrices.pro.monthly}/mo` : <Skeleton />, 'Custom'] },
      { label: 'Annual price',  values: ['Free', plans?.displayPrices?.starter?.annual ? `${plans.displayPrices.starter.annual}/mo` : <Skeleton />, plans?.displayPrices?.pro?.annual ? `${plans.displayPrices.pro.annual}/mo` : <Skeleton />, 'Custom'],
        sub:                             [null,   'Billed annually', 'Billed annually', null] },
    ],
  },
  {
    id: 'api', title: 'API Limits',
    underline: false,
    rows: [{ label: 'Monthly API calls', values: ['1,000', '50,000', '1,000,000', 'Unlimited'] }],
  },
  {
    id: 'history', title: 'Historical Data',
    underline: false,
    rows: [{ label: 'Record availability', values: ['1 Year', '25 Years', '+50 (unlimited)', 'Unlimited'] }],
  },
  {
    id: 'data', title: 'Data Access',
    underline: true,
    rows: [
      { label: 'Stocks',                  values: [true,  true,  true,  true], link: '/products/stocks'},
      { label: 'ETFs',                    values: [false, true,  true,  true], link: '/products/etfs'},
      { label: 'Forex',                   values: [false, true,  true,  true], link: '/products/forex'},
      { label: 'Futures',                 values: [false, false, true,  true], link: '/products/futures'},
      { label: 'Indices',                 values: [false, true,  true,  true], link: '/products/indices'},
      { label: 'Crypto',                  values: [false, true,  true,  true], link: '/products/crypto'},


      { label: 'Corporate Intelligence',        values: [true,  true,  true,  true], link: '/products/corporate-profiles'},
      { label: 'Earnings',                values: [false,  true,  true,  true], link: '/products/earnings'},
      { label: 'Filings & Financials',   values: [false,  true,  true,  true], link: '/products/disclosures'},
      { label: 'Economic Data',           values: [false, true,  true,  true], link: '/products/economics'},
      { label: 'News',                    values: [false, true,  true,  true], link: '/products/news'},


      { label: 'Credit Ratings',          values: [false, false, true,  true], link: '/products/credit'},
      { label: 'ESG Rankings',            values: [false, false, true,  true], link: '/products/esg'},
      { label: 'Supply Chain',            values: [false, false, true,  true], link: '/products/supply-chain'},
      { label: 'Sentiment Analysis',      values: [false, false, true,  true], link: '/products/sentiment'},
      { label: 'Web Traffic',             values: [false, false, true,  true], link: '/products/webtraffic'},

    ],
  },
];

/* ══════════════════════════════════════
   DESKTOP  (> medium)
══════════════════════════════════════ */
const DesktopTable = styled.div`
  border: ${BORDER};
  border-radius: 8px;
  overflow: hidden;
  @media (max-width: ${breakpoints.medium}) { display: none; }
`;

const DGrid = styled.div`
  display: grid;
  grid-template-columns: 220px repeat(4, 1fr);
`;

const DHeaderRow = styled(DGrid)`border-bottom: ${BORDER};`;

const DPlanCell = styled.div`
  padding: 20px 16px;
  text-align: center;
  border-left: ${BORDER};
`;

const DPlanName = styled.div`
  font-weight: 600;
  font-size: 15px;
  margin-bottom: 12px;
`;

const DSectionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px;
  border-top: ${BORDER};
  cursor: pointer;
  user-select: none;
  &:hover { background: #fafafa; }
`;

const DFeatureRow = styled(DGrid)`border-top: ${BORDER};`;

const DLabelCell = styled.div`
  padding: 14px 24px;
  display: flex;
  align-items: center;
  color: #374151;
  font-size: 14px;
`;

const DLabel = styled(Link)`
  ${props => (props.$underline) ?
  'border-bottom: 1.5px dashed #9ca3af;' : ''
  }

  cursor: pointer;
`;

const DValueCell = styled.div`
  border-left: ${BORDER};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px 16px;
  color: #374151;
  font-size: 14px;
`;

/* ══════════════════════════════════════
   MOBILE  (≤ medium)
   4 plan blocks stacked, each collapsible
══════════════════════════════════════ */
const MobileStack = styled.div`
  display: none;
  flex-direction: column;
  @media (max-width: ${breakpoints.medium}) { display: flex; }
`;

const MPlanBlock = styled.div`
  border: ${BORDER};
  border-radius: 8px;
  overflow: hidden;
  & + & { margin-top: 12px; }
`;

/* Plan name row - full width, tap to collapse/expand */
const MPlanToggle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  cursor: pointer;
  user-select: none;
  background: #fff;
  &:hover { background: #fafafa; }
`;

const MPlanName = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: #111;
`;

/* Section header inside a plan block */
const MSectionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 20px;
  border-top: ${BORDER};
  cursor: pointer;
  user-select: none;
  background: #fff;
  &:hover { background: #fafafa; }
`;

const MSectionTitle = styled.span`
  font-weight: 700;
  font-size: 14px;
`;

/* 2-col feature row - label left, value right, matching screenshot 50/50 split */
const MFeatureRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-top: ${BORDER};
  min-height: 52px;
`;

const MLabelCell = styled.div`
  display: flex;
  align-items: center;
  padding: 14px 20px;
  color: #374151;
  font-size: 14px;
`;

const MLabel = styled.span`
  ${({ $d }) => $d && 'border-bottom: 1.5px dashed #9ca3af; cursor: default;'}
`;

const MValueCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-left: ${BORDER};
  padding: 14px 16px;
  color: #374151;
  font-size: 14px;
  text-align: center;
`;

/* ══════════════════════════════════════
   Component
══════════════════════════════════════ */
const Wrap = styled.div`
  @keyframes skeletonPulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }
  font-size: 14px;
  color: #1a1a1a;
  background: #fff;
  padding: 40px 24px;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: ${breakpoints.medium}) { padding: 20px 16px; }
  @media (max-width: ${breakpoints.small})  { padding: 16px 12px; }

  .pricing-copy{
    display: flex;
    align-items:center;
    justify-content:center;
    flex-direction: column;
    text-align: center;
    padding: 0 40px;
    padding-bottom: 80px;
    h2{
      font-size: 42px;
      padding: 32px 0;
      width: 80%;
      @media (max-width: ${breakpoints.small}) {
          width: 100%;
      }
    }
    p{
      color: #595959;
      line-height: 1.4;
    }

    @media (max-width: ${breakpoints.medium}) {
      display: block;
      align-items:center;
      justify-content:center;
      text-align: left;
      padding: 0 20px;
      padding-bottom: 40px;

    }
  }

`;

const PricingTable = ({title='Pricing That Scales With You', subtitle='Flexible plans for developers, startups, and enterprises. Access comprehensive market data, fundamental data, and alternative data through a single financial data API.'}) => {
  const purchase = usePurchase();
  const navigate = useNavigate();
  const { plans: serverPlans } = usePlans();
  const sections = buildSections(serverPlans);


  const [sectionCollapsed, setSectionCollapsed] = useState({});
  const [planCollapsed,    setPlanCollapsed]    = useState({});

  const toggleSection = (id) => setSectionCollapsed((p) => ({ ...p, [id]: !p[id] }));
  const togglePlan    = (name) => setPlanCollapsed((p) => ({ ...p, [name]: !p[name] }));

 const buy = (plan: string) => {
    purchase.capturePurchaseIntent(plan, 'monthly');
  };

  const CTAS = {
    free: () => { navigate('/login') },
    starter: () => { buy('starter') },
    pro: () => { buy('pro') },
    enterprise: () => { navigate('/contact') },
  }


  return (
    <Wrap>
      <div className="pricing-copy">
        <h2 className="serif"> {title}</h2>
        <p>{subtitle}</p>
      </div>

      {/* ── DESKTOP ── */}
      <DesktopTable>
        <DHeaderRow>
          <div />
          {planDefs.map(({ name, btn, variant, id }) => (
            <DPlanCell key={name}>
              <DPlanName>{name}</DPlanName>
              <Btn
                $v={variant}
                onClick={() => { CTAS[id]() }}
              >{btn}</Btn>
            </DPlanCell>
          ))}
        </DHeaderRow>

        {sections.map((section) => {
          const isOpen = !sectionCollapsed[section.id];
          return (
            <React.Fragment key={section.id}>
              <DSectionRow onClick={() => toggleSection(section.id)}>
                <span style={{ fontWeight: 600, fontSize: 15 }}>{section.title}</span>
                <CollapseIcon open={isOpen} />
              </DSectionRow>
              {isOpen && section.rows.map((row, i) => (
                <DFeatureRow key={i}>
                  <DLabelCell><DLabel to={row.link} $underline={section.underline}>{row.label}</DLabel></DLabelCell>
                  {row.values.map((val, j) => (
                    <DValueCell key={j}>{renderValue(val, row.sub?.[j])}</DValueCell>
                  ))}
                </DFeatureRow>
              ))}
            </React.Fragment>
          );
        })}
      </DesktopTable>

      {/* ── MOBILE: 4 collapsible plan blocks ── */}
      <MobileStack>
        {planDefs.map(({ name, btn, variant, id }, planIdx) => {
          const isPlanOpen = !planCollapsed[name];
          return (
            <MPlanBlock key={name}>

              {/* Plan name - tap to collapse entire plan */}
              <MPlanToggle onClick={() => togglePlan(name)}>
                <MPlanName>{name}</MPlanName>
                <UpDownIcon />
              </MPlanToggle>

              {isPlanOpen && (
                <>
                  {/* CTA */}
                  <div style={{ padding: '0 20px 16px', borderTop: BORDER, paddingTop: 16 }}>
                    <Btn
                      $v={variant}
                      onClick={() => { CTAS[id]() }}
                    > {btn}</Btn>
                  </div>

                  {/* Sections */}
                  {sections.map((section) => {
                    const isOpen = !sectionCollapsed[section.id];
                    return (
                      <React.Fragment key={section.id}>
                        <MSectionRow onClick={() => toggleSection(section.id)}>
                          <MSectionTitle>{section.title}</MSectionTitle>
                          <CollapseIcon open={isOpen} />
                        </MSectionRow>

                        {isOpen && section.rows.map((row, i) => (
                          <MFeatureRow key={i}>
                            <MLabelCell>
                              <MLabel $d={row.dotted}>{row.label}</MLabel>
                            </MLabelCell>
                            <MValueCell>
                              {renderValue(row.values[planIdx], row.sub?.[planIdx])}
                            </MValueCell>
                          </MFeatureRow>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </>
              )}
            </MPlanBlock>
          );
        })}
      </MobileStack>

    </Wrap>
  );
};

export default PricingTable;
